import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight } from 'lucide-react';
import api, { bookingsApi } from '../../services/api';
import { formatDate, formatCurrency } from '../../lib/utils';
import { Button, Kpi, Badge, Card, EmptyState, Tabs } from '../../components/sv';
import { cargoLabel } from '../../components/booking/BulkFields';

const STATUS_LABEL = { pending: 'Pending', confirmed: 'Confirmed', in_transit: 'In transit', delivered: 'Delivered', cancelled: 'Cancelled' };

export function BridgeOverview({ role }) {
  const base = role === 'admin' ? '/admin' : '/agent';
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  useEffect(function () {
    Promise.all([api.get(base + '/stats'), bookingsApi.getAll(), api.get('/users/pending')])
      .then(function (r) { setStats(r[0].data); setBookings(r[1].data); setPendingUsers(r[2].data || []); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }, [base]);

  const s = stats || {};
  const unassigned = bookings.filter(function (b) { return b.status === 'confirmed' && b.shipment_mode !== 'bulk' && (!b.container_numbers || b.container_numbers.length === 0); });
  const filtered = tab === 'all' ? bookings : tab === 'pending' ? bookings.filter(function (b) { return b.status === 'pending'; }) : tab === 'transit' ? bookings.filter(function (b) { return b.status === 'in_transit'; }) : bookings.filter(function (b) { return b.shipment_mode === 'bulk'; });

  return (
    <div className="grid gap-6" data-testid={role + '-dashboard'}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="sv-kicker">{role === 'admin' ? 'Global overview' : 'Regional overview'} · {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <h1 className="text-[32px] sv-display mt-1">Operations</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" to={base + '/approvals'} data-testid="overview-approvals-btn">Approvals{pendingUsers.length ? ' · ' + pendingUsers.length : ''}</Button>
          <Button to={base + '/walkin-booking'} data-testid="overview-walkin-btn">New booking</Button>
        </div>
      </div>

      {loading ? <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-600" /></div> : (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <Kpi label="Total bookings" value={s.total_bookings ?? bookings.length} data-testid="stat-total-bookings" />
            <Kpi label="Pending confirmation" value={s.pending_bookings ?? 0} tone={s.pending_bookings ? 'bad' : undefined} delta={s.pending_bookings ? 'action required' : undefined} data-testid="stat-pending" />
            <Kpi label="In transit" value={s.in_transit_bookings ?? 0} data-testid="stat-in-transit" />
            <Kpi label={role === 'admin' ? 'Registered users' : 'Customers'} value={role === 'admin' ? (s.total_users ?? 0) : (s.total_customers ?? 0)} delta={pendingUsers.length ? pendingUsers.length + ' awaiting approval' : undefined} tone={pendingUsers.length ? 'bad' : undefined} data-testid="stat-people" />
            <Kpi label="Revenue" value={formatCurrency(s.revenue || 0)} data-testid="stat-revenue" />
          </div>

          {role === 'admin' && (
            <div className="grid sm:grid-cols-3 gap-4">
              <Kpi label="Vessels" value={s.total_vessels ?? 0} delta={(s.active_vessels ?? 0) + ' active'} data-testid="stat-vessels" />
              <Kpi label="Quotes issued" value={s.total_quotes ?? 0} data-testid="stat-quotes" />
              <Kpi label="Ports served" value={s.total_ports ?? 0} data-testid="stat-ports" />
            </div>
          )}

          <div className="grid lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)] gap-6 items-start">
            <div className="sv-card" data-testid="bridge-bookings">
              <div className="flex items-center justify-between px-6 pt-4 border-b border-gray-200">
                <Tabs active={tab} onChange={setTab} className="border-0" tabs={[{ id: 'all', label: 'All bookings', count: bookings.length }, { id: 'pending', label: 'Pending', count: s.pending_bookings ?? 0 }, { id: 'transit', label: 'In transit' }, { id: 'bulk', label: 'Bulk' }]} />
                <Link to={base + '/bookings'} className="sv-ink-link text-[15px] pb-3">Manage <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
              </div>
              {filtered.length === 0 ? <EmptyState title="No bookings in this view" /> : (
                <div className="overflow-x-auto">
                <table className="sv-table sv-table-dense">
                  <thead><tr><th>Booking</th><th>Customer</th><th>Route</th><th>Cargo</th><th>Status</th><th>Departs</th><th className="text-right">Freight</th></tr></thead>
                  <tbody>
                    {filtered.slice(0, 8).map(function (b) {
                      return (
                        <tr key={b.id} data-testid={"bridge-booking-" + b.id}>
                          <td><Link to={base + '/bookings'} className="sv-mono text-[13px]">{b.booking_number}</Link></td>
                          <td>{b.customer_name || b.user_email || '—'}{b.customer_company && <div className="text-[13px] text-gray-600">{b.customer_company}</div>}</td>
                          <td className="whitespace-nowrap">{b.origin_port.split(',')[0]} → {b.destination_port.split(',')[0]}</td>
                          <td className="capitalize whitespace-nowrap">{cargoLabel(b)}</td>
                          <td><Badge status={b.status}>{STATUS_LABEL[b.status] || b.status}</Badge></td>
                          <td className="sv-mono text-[13px] whitespace-nowrap">{formatDate(b.pickup_date)}</td>
                          <td className="text-right sv-mono text-[13px]">{formatCurrency(b.price)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </div>

            <div className="grid gap-6">
              <Card title="Work queue" data-testid="work-queue">
                <div className="grid gap-3 text-[15px]">
                  {[
                    [pendingUsers.length, 'account approvals waiting', base + '/approvals'],
                    [s.pending_bookings ?? 0, 'bookings to confirm', base + '/bookings'],
                    [unassigned.length, 'confirmed bookings without containers', base + '/containers'],
                  ].map(function (q) {
                    return (
                      <Link key={q[1]} to={q[2]} className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3 first:border-0 first:pt-0 hover:text-blue-600">
                        <span><span className={"sv-mono mr-2 " + (q[0] ? 'text-error-700' : 'text-gray-600')}>{q[0]}</span>{q[1]}</span>
                        <ArrowRight className="h-4 w-4 text-gray-600" strokeWidth={1.75} />
                      </Link>
                    );
                  })}
                </div>
              </Card>
              <Card title="Pending approvals" action={<Link to={base + '/approvals'} className="text-sm text-gray-600 hover:text-ink">All</Link>} data-testid="pending-approvals-card">
                {pendingUsers.length === 0 ? <p className="text-[15px] text-gray-700">No accounts waiting for review.</p> : (
                  <div className="grid gap-3">
                    {pendingUsers.slice(0, 4).map(function (u) {
                      return <div key={u.id} className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3 first:border-0 first:pt-0 text-[15px]"><div><div>{u.first_name} {u.last_name}</div><div className="text-[13px] text-gray-600">{u.company || u.email} · {u.country}</div></div><Badge status="pending_approval">Pending</Badge></div>;
                    })}
                  </div>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
